import type { InsuranceSearchResult } from '../../../../../hooks/useSearchInsurance';
import type { SubCompanyMode } from '../../../../../components/settings/SubCompanyTable';
import {
    dropOverlappingLkqPricingEntries,
    removeLkqPricingRule,
    toLkqPricingEntries,
    type LkqPricingEntryFactory,
    type LkqPricingRowValues,
    type LkqPricingRule,
} from '../../../../../utils/lkqPricingRows';
import {
    LKQ_PRICING_ALL_LABEL,
    LKQ_PRICING_WILDCARD,
    sortLkqPricingStates,
} from '../../../../../utils/lkqPricingConstants';
import type { PricingEntry } from './pricingTables';

/** The pricing rule screen's name for a group company's fate; see SubCompanyMode. */
export type PricingRuleSubMode = SubCompanyMode;

/** One pricing rule being added or edited, before anything is saved. */
export interface PricingRuleDraft {
    /**
     * The percentage as a whole number, kept as text so a half-typed value
     * survives (e.g. "2" on the way to "25").
     */
    percent: string;
    /** Supplier id; '' until one is picked, '*' for every vendor. */
    supplierId: string;
    supplierName: string;
    /** Empty means used and aftermarket. */
    partKind: string;
    /** Empty means any vehicle make. */
    make: string;
    /** Insurer id; '' until one is picked, '*' for every insurance company. */
    insuranceCompanyId: string;
    insuranceCompanyName: string;
    /** Group the company belongs to; what its sub companies are looked up by. */
    insuranceGroupId?: string;
    /**
     * The group's parent, when this company is itself a branched sub company.
     * Only a label — it is what makes the row read "Allstate -> SafeAuto".
     */
    parentInsuranceCompanyName?: string;
    /** What to do with each company in the group, by insurer id. */
    subModes: Record<string, PricingRuleSubMode>;
    /**
     * Set when the user folds this company into a group mate that already has a
     * rule: no new rule is written, it just follows that one.
     */
    joinParentId?: string;
    joinParentName?: string;
    /** Selected state codes, sorted. `['*']` means every state. */
    states: string[];
}

export const wholePercentToDecimal = (value: number): number => value / 100;
export const decimalToWholePercent = (value: number): number => Math.round(value * 100);

export const buildPricingRuleDraft = (rule?: LkqPricingRule): PricingRuleDraft => ({
    percent: rule != null ? String(decimalToWholePercent(rule.percentage)) : '',
    supplierId: rule?.supplierId ?? '',
    supplierName: rule?.supplierName ?? '',
    partKind: rule?.partKind ?? '',
    make: rule?.make ?? '',
    insuranceCompanyId: rule?.insuranceCompanyId ?? '',
    insuranceCompanyName: rule?.insuranceCompanyName ?? '',
    insuranceGroupId: rule?.insuranceGroupId,
    parentInsuranceCompanyName: rule?.parentInsuranceCompanyName,
    subModes: {},
    joinParentId: undefined,
    joinParentName: undefined,
    states: rule?.states ?? [],
});

export const isAllVendors = (draft: PricingRuleDraft): boolean =>
    draft.supplierId === LKQ_PRICING_WILDCARD;

export const isAllCompanies = (draft: PricingRuleDraft): boolean =>
    draft.insuranceCompanyId === LKQ_PRICING_WILDCARD;

export const isAllStates = (draft: PricingRuleDraft): boolean =>
    draft.states.includes(LKQ_PRICING_WILDCARD);

/** The wildcard selection for a dimension the rule should not narrow. */
export const ALL_SELECTION = {
    id: LKQ_PRICING_WILDCARD,
    name: LKQ_PRICING_ALL_LABEL,
} as const;

/** The typed percentage, or null while it is blank or out of range. */
export const draftPercent = (draft: PricingRuleDraft): number | null => {
    const trimmed = draft.percent.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100) return null;
    return parsed;
};

const draftPartKind = (draft: PricingRuleDraft): string => draft.partKind.trim().toLowerCase();

/** The rule's stored values, or null while the draft is still incomplete. */
export const pricingRuleValues = (draft: PricingRuleDraft): LkqPricingRowValues | null => {
    const percent = draftPercent(draft);
    if (percent === null) return null;
    if (!draft.supplierId || !draft.insuranceCompanyId || draft.states.length === 0) return null;
    return {
        supplierId: draft.supplierId,
        supplierName: draft.supplierName,
        ...(draftPartKind(draft) ? { partKind: draftPartKind(draft) } : {}),
        ...(draft.make?.trim() ? { make: draft.make.trim() } : {}),
        states: isAllStates(draft) ? [LKQ_PRICING_WILDCARD] : sortLkqPricingStates(draft.states),
        percentage: wholePercentToDecimal(percent),
    };
};

/**
 * The mode each group company starts on. Companies already on this rule keep
 * the shape they were saved in, so opening a rule shows the truth rather than a
 * proposal. Ones with no rule of their own default to riding along when a rule
 * is first added — a group is normally taken on whole — and to being left alone
 * when an existing rule is edited, so a later visit never quietly widens what
 * the shop has agreed.
 *
 * 'separate' is never seeded: branching is something the user does here, and a
 * company that already carries its own rule is 'elsewhere'.
 */
export const seedPricingRuleSubModes = (
    relatedCompanies: Array<InsuranceSearchResult>,
    rules: Array<LkqPricingRule>,
    rule: LkqPricingRule | undefined,
    isAddMode: boolean,
): Record<string, PricingRuleSubMode> => {
    const included = new Set(rule?.includedCompanies.map((company) => company.id) ?? []);
    const owners = new Set(rules.map(({ insuranceCompanyId }) => insuranceCompanyId));
    const modes: Record<string, PricingRuleSubMode> = {};

    for (const company of relatedCompanies) {
        if (included.has(company._id)) {
            modes[company._id] = 'same';
            continue;
        }
        modes[company._id] = owners.has(company._id) ? 'elsewhere' : isAddMode ? 'same' : 'none';
    }

    return modes;
};

/** Which company a group mate's own row follows, for the table's label. */
export const pricingRuleFollowsName = (
    rules: Array<LkqPricingRule>,
    insurerId: string,
): string | undefined =>
    rules.find(
        (rule) =>
            rule.includedCompanies.some((company) => company.id === insurerId) &&
            rule.insuranceCompanyId !== insurerId,
    )?.insuranceCompanyName;

export interface BuildPricingRuleEntriesArgs {
    /** The saved list this save is merged into. */
    entries: Array<PricingEntry>;
    draft: PricingRuleDraft;
    /** Companies in the primary's group, the primary itself excluded. */
    relatedCompanies: Array<InsuranceSearchResult>;
    percentageOf: (entry: PricingEntry) => number;
    buildEntry: LkqPricingEntryFactory<PricingEntry>;
    /** The rule being edited; its entries are replaced. */
    originalRule?: LkqPricingRule;
    /** The group mate's rule this one folds into, when merging. */
    mergeTargetRule?: LkqPricingRule;
}

/**
 * The whole table as it should look after this screen saves. POST
 * /settings/update replaces the invoicing subdocument outright, so the merge
 * happens here.
 *
 * One entry per company, vendor and state is kept — that is what the invoicing
 * pipeline looks a line item up by — and the grouping rides along in
 * `parentInsuranceCompanyId`: sub companies on the rule point at the primary,
 * branched ones stand on their own and only borrow the primary's name.
 */
export const buildPricingRuleEntries = ({
    entries,
    draft,
    relatedCompanies,
    percentageOf,
    buildEntry,
    originalRule,
    mergeTargetRule,
}: BuildPricingRuleEntriesArgs): Array<PricingEntry> => {
    let next = originalRule
        ? removeLkqPricingRule(entries, originalRule.ruleKey, percentageOf)
        : entries;

    // Merging writes no rule of its own: this company starts following the
    // group mate the user picked. Anything that was on this company's rule
    // moves across too, so the whole group lands on one row instead of trailing
    // off a rule that no longer exists.
    if (draft.joinParentId && mergeTargetRule) {
        const values: LkqPricingRowValues = {
            supplierId: mergeTargetRule.supplierId,
            supplierName: mergeTargetRule.supplierName,
            ...(mergeTargetRule.partKind ? { partKind: mergeTargetRule.partKind } : {}),
            ...(mergeTargetRule.make ? { make: mergeTargetRule.make } : {}),
            states: mergeTargetRule.states,
            percentage: mergeTargetRule.percentage,
        };
        const followers = [
            { id: draft.insuranceCompanyId, name: draft.insuranceCompanyName },
            ...(originalRule?.includedCompanies ?? []),
        ];
        next = dropOverlappingLkqPricingEntries(
            next,
            new Set(followers.map((company) => company.id)),
            values,
        );
        return [
            ...next,
            ...followers.flatMap((company) =>
                toLkqPricingEntries(
                    {
                        ...company,
                        insuranceGroupId: draft.insuranceGroupId ?? mergeTargetRule.insuranceGroupId,
                        parentInsuranceCompanyId: mergeTargetRule.insuranceCompanyId,
                        parentInsuranceCompanyName: mergeTargetRule.insuranceCompanyName,
                    },
                    values,
                    buildEntry,
                ),
            ),
        ];
    }

    const values = pricingRuleValues(draft);
    if (!values) return entries;

    const rows = toLkqPricingEntries(
        {
            id: draft.insuranceCompanyId,
            name: draft.insuranceCompanyName,
            insuranceGroupId: draft.insuranceGroupId,
            parentInsuranceCompanyName: draft.parentInsuranceCompanyName,
        },
        values,
        buildEntry,
    );

    for (const company of relatedCompanies) {
        const mode = draft.subModes[company._id] ?? 'none';
        // 'none' writes nothing — anything it had on this rule went with the
        // rule above. 'elsewhere' is not this screen's business: that company
        // carries its own rule, edited from its own row.
        if (mode === 'none' || mode === 'elsewhere') continue;
        rows.push(
            ...toLkqPricingEntries(
                {
                    id: company._id,
                    name: company.name,
                    insuranceGroupId: draft.insuranceGroupId,
                    // Riding along points at the primary, so it has no row of
                    // its own; branching keeps only the primary's name, which
                    // gives it a row to retune.
                    parentInsuranceCompanyId:
                        mode === 'same' ? draft.insuranceCompanyId : undefined,
                    parentInsuranceCompanyName: draft.insuranceCompanyName,
                },
                values,
                buildEntry,
            ),
        );
    }

    // Whatever this rule now covers replaces what those companies held before,
    // so a company pulled in from its own rule cannot end up with two.
    next = dropOverlappingLkqPricingEntries(
        next,
        new Set(rows.map((row) => row.insuranceCompanyId)),
        values,
    );

    return [...next, ...rows];
};
